import {
    z,
} from 'zod';
import { ArgoConnectionConfigStatusEnum, ArgoConnectionKindEnum } from '../db';
import { serialConnectionConfigSchema } from './serial.connection-config.zod';
import { tcpClientConnectionConfigSchema } from './tcp-client.connection-config.zod';
import { tcpServerConnectionConfigSchema } from './tcp-server.connection-config.zod';
import { udpSocketConnectionConfigSchema } from './udp-socket.connection-config.zod';
import { mqttBrokerConnectionConfigSchema } from './mqtt-broker.connection-config.zod';
import { ptl110PickLightConnectionConfigSchema } from './ptl110-pick-light.connection-config.zod';
import { ioLinkMasterConnectionConfigSchema } from './io-link-master.connection-config.zod';
import { openProtocolConnectionConfigSchema } from './open-protocol.connection-config.zod';
import { torqueToolConnectionConfigSchema } from './torque-tool.connection-config.zod';

export const DEFAULT_CONNECTION_ID = '019ebd21-0489-7249-bbfd-081a691bb801';

/** @description this is the object that gets passed from a connection instance to RunnerInspection each time the connection has something to log */
export class ConnectionLog {
    message: string;
    verbose: boolean;
    timestamp: number;
    connectionName: string;
    logLevel: 'info' | 'warning' | 'error';
    connectionKind: ArgoConnectionKindEnum;
    
    constructor (payload: {
        message: string;
        verbose: boolean;
        timestamp: number;
        connectionName: string;
        logLevel: 'info' | 'warning' | 'error';
        connectionKind: ArgoConnectionKindEnum;
    }) {
        this.message = payload.message;
        this.verbose = payload.verbose;
        this.logLevel = payload.logLevel;
        this.timestamp = payload.timestamp;
        this.connectionName = payload.connectionName;
        this.connectionKind = payload.connectionKind;
    }
}

/** @description this is the object that gets passed to RunnerInspection to show the connection status updates*/
export type ConnectionStatus = {
    name: string;
    isConnected: boolean;
    versionNumber: number;
    connectionErrorMsg?: string;
    kind: ArgoConnectionKindEnum;
};
export type ConnectionStatuses = {
    [connectionId: string]: ConnectionStatus;
};

// utility type to that will throw an error when a new connection kind is added to help remind me that I need to add a schema below
const _connectionConfigDetailsMap = {
    serial: null,
    tcpClient: null,
    tcpServer: null,
    udpSocket: null,
    mqttBroker: null,
    torqueTool: null,
    ioLinkMaster: null,
    openProtocol: null,
    ptl110PickLight: null,
} satisfies {
    [K in ArgoConnectionKindEnum]: null;
};

export const connectionConfigDetailsSchema = z.discriminatedUnion('kind', [
    serialConnectionConfigSchema,
    tcpClientConnectionConfigSchema,
    tcpServerConnectionConfigSchema,
    udpSocketConnectionConfigSchema,
    torqueToolConnectionConfigSchema,
    mqttBrokerConnectionConfigSchema,
    openProtocolConnectionConfigSchema,
    ioLinkMasterConnectionConfigSchema,
    ptl110PickLightConnectionConfigSchema,
]);

export type ConnectionConfigDetails = z.infer<typeof connectionConfigDetailsSchema>;
export type ConnectionConfig = ConnectionConfigDetails['details'];

export const connectionEntitySchema = z.object({
    id: z.string(),
    name: z.string(),
    connectionConfig: z.object({
        id: z.string(),
        config: connectionConfigDetailsSchema,
        status: z.nativeEnum(ArgoConnectionConfigStatusEnum),
    }),
});

export interface ConnectionEntity extends z.infer<typeof connectionEntitySchema> {}
